import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { 
  User,
  Mail,
  Shield,
  LogOut,
  Settings,
  Bell,
  Target,
  ChevronRight,
  Save,
  Camera,
  Award,
  Sparkles
} from "lucide-react";
import { toast } from "sonner";

const Profile = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [editingTargets, setEditingTargets] = useState(false);
  const [targets, setTargets] = useState({
    dailyCalorieTarget: 2000,
    proteinTarget: 150,
    carbsTarget: 200,
    fatsTarget: 65
  });
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [myBadges, setMyBadges] = useState([]);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    fetchMyBadges();
    if (user) {
      setTargets({
        dailyCalorieTarget: user.dailyCalorieTarget || 2000,
        proteinTarget: user.proteinTarget || 150,
        carbsTarget: user.carbsTarget || 200,
        fatsTarget: user.fatsTarget || 65
      });
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.filter(n => !n.read).slice(0, 5));
    } catch (err) {
      // Notifications endpoint might not exist
    }
  };

  const fetchMyBadges = async () => {
    try {
      const res = await axios.get(`${API}/badges/my`);
      setMyBadges(res.data);
    } catch (err) {
      console.error("Badges fetch error:", err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${API}/notifications/read-all`);
      setNotifications([]);
      toast.success("All notifications marked as read");
    } catch (err) {
      toast.error("Failed to mark notifications");
    }
  };

  const saveTargets = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/auth/targets`, targets);
      updateUser(res.data);
      toast.success("Targets updated");
      setEditingTargets(false);
    } catch (err) {
      toast.error("Failed to update targets");
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploadingPhoto(true);
    
    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const base64 = e.target?.result;
        await axios.put(`${API}/auth/profile-photo`, { profilePhoto: base64 });
        updateUser({ ...user, profilePhoto: base64 });
        toast.success("Profile photo updated");
      } catch (err) {
        toast.error("Failed to update photo");
      } finally {
        setUploadingPhoto(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    toast.success("Mā te wā - See you later!");
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?';
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-[#A8583C] text-white';
      case 'coach': return 'bg-[#D4A84B] text-[#051F17]';
      default: return 'bg-[#0B3D2E] text-white';
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'challenger': return 'bg-[#5B8BA0] text-white';
      case 'member': return 'bg-[#3D6B5A] text-white';
      default: return 'bg-[#8FA39B] text-white';
    }
  };

  const getBadgeIcon = (icon) => {
    const icons = {
      sparkles: Sparkles,
      flame: Target,
      'calendar-check': Bell,
      users: User,
      trophy: Award,
      award: Award
    };
    const Icon = icons[icon] || Award;
    return <Icon className="w-3 h-3" />;
  };

  return (
    <div className="p-4 pb-8 max-w-lg mx-auto" data-testid="profile">
      {/* Profile Header with Photo Upload */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative">
          <Avatar className="w-20 h-20 bg-[#0B3D2E]">
            {user?.profilePhoto ? (
              <AvatarImage src={user.profilePhoto} alt={user.name} />
            ) : null}
            <AvatarFallback className="bg-[#0B3D2E] text-white text-2xl">
              {getInitials(user?.name)}
            </AvatarFallback>
          </Avatar>
          
          {/* Photo Upload Button */}
          <input
            type="file"
            ref={fileInputRef}
            accept="image/*"
            onChange={handlePhotoSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingPhoto}
            className="absolute bottom-0 right-0 w-8 h-8 bg-[#0B3D2E] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#0B3D2E]/90 transition-colors"
            data-testid="upload-photo-btn"
          >
            {uploadingPhoto ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </button>
        </div>
        
        <div>
          <h1 className="text-xl font-brand text-[#0B3D2E]">{user?.name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge className={getRoleBadgeColor(user?.role)}>
              {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
            </Badge>
            <Badge className={getTierBadgeColor(user?.tier)}>
              {user?.tier?.charAt(0).toUpperCase() + user?.tier?.slice(1)}
            </Badge>
          </div>
        </div>
      </div>

      {/* Badges Card */}
      {myBadges.length > 0 && (
        <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4" data-testid="badges-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 font-brand">
              <Award className="w-4 h-4 text-[#D4A84B]" /> My Badges
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {myBadges.map((badge) => (
                <div 
                  key={badge.id}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4A84B]/10 text-[#D4A84B] rounded-full text-sm"
                >
                  {getBadgeIcon(badge.badgeIcon)}
                  <span className="font-medium">{badge.badgeName}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Info Card */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4" data-testid="user-info-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 font-brand">
            <User className="w-4 h-4 text-[#0B3D2E]" /> Account
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-3 py-2">
            <Mail className="w-4 h-4 text-[#8FA39B]" />
            <span className="text-sm text-[#051F17]">{user?.email}</span>
          </div>
          <div className="flex items-center gap-3 py-2">
            <Shield className="w-4 h-4 text-[#8FA39B]" />
            <span className="text-sm text-[#051F17] capitalize">{user?.role} Access</span>
          </div>
        </CardContent>
      </Card>

      {/* Nutrition Targets Card */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4" data-testid="targets-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 font-brand">
              <Target className="w-4 h-4 text-[#0B3D2E]" /> Daily Targets
            </CardTitle>
            {editingTargets ? (
              <Button 
                size="sm" 
                onClick={saveTargets}
                disabled={saving}
                className="bg-[#0B3D2E] text-white h-8 text-xs rounded-full"
              >
                {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <><Save className="w-3 h-3 mr-1" /> Save</>}
              </Button>
            ) : (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setEditingTargets(true)}
                className="text-[#0B3D2E] text-xs"
              >
                Edit
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#4A5D56]">Calories</Label>
              <Input
                type="number"
                value={targets.dailyCalorieTarget}
                onChange={(e) => setTargets({...targets, dailyCalorieTarget: parseInt(e.target.value) || 0})}
                disabled={!editingTargets}
                className="h-10 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs text-[#4A5D56]">Protein (g)</Label>
              <Input
                type="number"
                value={targets.proteinTarget}
                onChange={(e) => setTargets({...targets, proteinTarget: parseInt(e.target.value) || 0})}
                disabled={!editingTargets}
                className="h-10 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs text-[#4A5D56]">Carbs (g)</Label>
              <Input
                type="number"
                value={targets.carbsTarget}
                onChange={(e) => setTargets({...targets, carbsTarget: parseInt(e.target.value) || 0})}
                disabled={!editingTargets}
                className="h-10 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-xs text-[#4A5D56]">Fats (g)</Label>
              <Input
                type="number"
                value={targets.fatsTarget}
                onChange={(e) => setTargets({...targets, fatsTarget: parseInt(e.target.value) || 0})}
                disabled={!editingTargets}
                className="h-10 bg-[#F9F9F7] border-0 rounded-xl"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notifications Card */}
      <Card className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4" data-testid="notifications-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2 font-brand">
              <Bell className="w-4 h-4 text-[#0B3D2E]" /> Notifications
              {notifications.length > 0 && (
                <span className="w-5 h-5 bg-[#B85C4C] text-white text-xs rounded-full flex items-center justify-center">
                  {notifications.length}
                </span>
              )}
            </CardTitle>
            {notifications.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={markAllRead}
                className="text-[#0B3D2E] text-xs"
              >
                Mark all read
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <p className="text-sm text-[#8FA39B] text-center py-4">
              No new notifications
            </p>
          ) : (
            <div className="space-y-2">
              {notifications.map((notif) => (
                <div key={notif.id} className="p-3 bg-[#F9F9F7] rounded-xl">
                  <p className="font-medium text-sm text-[#051F17]">{notif.title}</p>
                  <p className="text-xs text-[#4A5D56]">{notif.body}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coach/Admin Access */}
      {["coach", "admin"].includes(user?.role) && (
        <Card 
          className="bg-white border border-[#0B3D2E]/5 rounded-2xl mb-4 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => navigate("/admin")}
          data-testid="admin-access-card"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#0B3D2E]/10 rounded-full flex items-center justify-center">
                <Settings className="w-5 h-5 text-[#0B3D2E]" />
              </div>
              <div>
                <p className="font-medium text-[#051F17]">Coach Dashboard</p>
                <p className="text-xs text-[#4A5D56]">View member progress & manage badges</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-[#8FA39B]" />
          </CardContent>
        </Card>
      )}

      {/* Privacy Notice */}
      <div className="p-4 bg-[#E3EBE5]/30 rounded-xl mb-4">
        <p className="text-xs text-[#4A5D56] text-center">
          Your journals and progress are visible to your Coach and Admin only. 
          Your privacy is respected.
        </p>
      </div>

      {/* Sign Out Button */}
      <Button 
        variant="outline"
        onClick={handleLogout}
        className="w-full h-12 border-[#B85C4C]/30 text-[#B85C4C] hover:bg-[#B85C4C]/5 rounded-xl"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4 mr-2" /> Sign Out
      </Button>
    </div>
  );
};

export default Profile;
